(() => {
  const ROOT_SELECTOR = '[data-ct-tabs]';
  const TAB_SELECTOR = '[data-ct-tab]';
  const PANEL_SELECTOR = '[data-ct-tab-panel]';

  class CTTabs {
    constructor(root) {
      this.root = root;
      this.tabs = Array.from(root.querySelectorAll(TAB_SELECTOR));
      this.panels = Array.from(root.querySelectorAll(PANEL_SELECTOR));

      if (!this.tabs.length || !this.panels.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.activate(tab);
        });

        tab.addEventListener('keydown', (event) => {
          this.handleKeyboard(event, tab);
        });
      });

      const selectedTab =
        this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ||
        this.tabs[0];

      this.activate(selectedTab, false);
    }

    activate(activeTab, shouldFocus = true) {
      if (!activeTab) return;

      const panelId = activeTab.getAttribute('aria-controls');
      const activePanel = this.panels.find((panel) => panel.id === panelId);

      this.hydratePanel(activePanel);

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;

        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== panelId;
      });

      if (shouldFocus) {
        activeTab.focus();

        activeTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }

    hydratePanel(panel) {
      if (!panel || panel.dataset.ctTabLoaded === 'true') return;

      const template = panel.querySelector('template[data-ct-tab-template]');

      if (!template) {
        panel.dataset.ctTabLoaded = 'true';
        return;
      }

      panel.appendChild(template.content.cloneNode(true));
      template.remove();
      panel.dataset.ctTabLoaded = 'true';

      window.CTWishlist?.refresh(panel);

      document.dispatchEvent(
        new CustomEvent('ct:tab:loaded', {
          detail: { panel },
        })
      );
    }

    handleKeyboard(event, currentTab) {
      const currentIndex = this.tabs.indexOf(currentTab);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex =
          (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.activate(this.tabs[nextIndex]);
    }
  }

  const initialize = (scope = document) => {
    const roots = [];

    if (scope.matches?.(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
      roots.push(root);
    });

    roots.forEach((root) => {
      if (root.dataset.ctTabsReady === 'true') return;

      root.dataset.ctTabsReady = 'true';
      root.ctTabsInstance = new CTTabs(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initialize();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest(PANEL_SELECTOR);

    if (!panel) return;

    const root = panel.closest(ROOT_SELECTOR);

    if (!root?.ctTabsInstance) return;

    const tab = root.ctTabsInstance.tabs.find(
      (item) => item.getAttribute('aria-controls') === panel.id
    );

    if (tab) {
      root.ctTabsInstance.activate(tab, false);
    }
  });
})();
